import { useContext, useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { WorkspaceBidderContext } from 'features/globalSetup/contexts/WorkspaceBidderContext'
import { Schema } from '../../form/schema'
import { useExistingWorkspaceBidder } from '../../hooks/useExistingWorkspaceBidder'
import { DomainBidderSlice, useGetDomainBidder } from '../../hooks/useGetDomainBidder'
import { useGetDomainBiddersForSelect } from '../../hooks/useGetDomainBiddersForSelect'
import { UseDomainBidderData } from './types'

type SchemaSlice = Pick<Schema, 'csEnabled' | 'domainIds' | 'priority' | 's2sEnabled'>

export const useDomainBidderData = (): UseDomainBidderData => {
  const [loading, setLoading] = useState<boolean>(false)
  const { setCurrentDomainBidder } = useContext(WorkspaceBidderContext)

  const {
    workspaceBidder: { id: workspaceBidderId },
  } = useExistingWorkspaceBidder()

  const { loading: loadingOptions, options } = useGetDomainBiddersForSelect({
    workspaceBidderId,
  })

  const { getValues, reset, setValue } = useFormContext<SchemaSlice>()
  const formValues = getValues()
  const {
    domainIds: { domainBidderId },
  } = useWatch()

  const { domainBidder, loading: loadingDomainBidder } = useGetDomainBidder({
    id: domainBidderId.value,
  })

  const selectFirstOption = (): void =>
    setValue('domainIds', {
      domainBidderId: options[0],
      domainId: options[0].rawData!.domain.id,
    })

  const setDomainBidderParams = (domainBidder: DomainBidderSlice): void => {
    const { csEnabled, priority, s2sEnabled } = domainBidder

    reset({
      ...formValues,
      csEnabled,
      priority,
      s2sEnabled,
    })
  }

  useEffect(() => {
    if (loadingDomainBidder || loadingOptions) {
      setLoading(true)
    } else {
      setLoading(false)
    }
  }, [loadingDomainBidder, loadingOptions])

  useEffect(() => {
    if (options.length > 0 && domainBidderId.value === '') {
      selectFirstOption()
    }
  }, [options])

  useEffect(() => {
    if (domainBidder) {
      setDomainBidderParams(domainBidder)

      if (setCurrentDomainBidder) {
        setCurrentDomainBidder(domainBidder)
      }
    }
  }, [domainBidder])

  return {
    domainBidder,
    loading,
    options,
  }
}
